import { Alert } from 'src/design/atoms/Alert';
import { Group } from 'src/design/atoms/Group';
import { SimpleGrid } from 'src/design/atoms/SimpleGrid';
import { Stack } from 'src/design/atoms/Stack';
import { Title } from 'src/design/atoms/Title';
import { NumberInput } from 'src/design/atoms/NumberInput';
import { Select } from 'src/design/atoms/Select';
import { TextInput } from 'src/design/atoms/TextInput';
import { DateInput } from 'src/design/atoms/DateInput';
import { useFormik } from 'formik';
import React from 'react';
import { ClassificationDto, UserRateDto } from 'src/api/FiretrolApiClient';
import { useClassifications } from 'src/api/queries/organization/useClassifications';
import { useUpdateUserRate } from 'src/api/queries/organization/useUserRateMutations';
import { SaveButton } from 'src/design/atoms/SaveButton';
import { SecondaryButton } from 'src/design/atoms/SecondaryButton';
import { getErrorMessage, formatRate } from 'src/utils';
import * as Yup from 'yup';

const validationSchema = Yup.object().shape({
  classificationId: Yup.string().required('Classification is required'),
  classificationLevelId: Yup.string().nullable(),
  rateImportKey: Yup.string().nullable(),
  hourlyRate: Yup.number()
    .min(0, 'Hourly rate must be 0 or greater')
    .required('Hourly rate is required'),
  hireDate: Yup.date().nullable(),
  healthInsurance: Yup.number()
    .min(0, 'Health insurance must be 0 or greater')
    .required('Health insurance is required'),
  basicLife: Yup.number()
    .min(0, 'Basic life must be 0 or greater')
    .required('Basic life is required'),
  accidentalDeath: Yup.number()
    .min(0, 'AD&D must be 0 or greater')
    .required('AD&D is required'),
  holiday: Yup.number()
    .min(0, 'Holiday must be 0 or greater')
    .required('Holiday is required'),
  _401k: Yup.number()
    .min(0, '401K must be 0 or greater')
    .required('401K is required'),
  vac: Yup.number()
    .min(0, 'VAC must be 0 or greater')
    .required('VAC is required'),
});

interface EditUserRateFormProps {
  userRate: UserRateDto;
  onSave: () => void;
  onCancel: () => void;
}

export const EditUserRateForm: React.FC<EditUserRateFormProps> = ({
  userRate,
  onSave,
  onCancel,
}) => {
  const [error, setError] = React.useState<string | null>(null);
  const { data: classificationData } = useClassifications();
  const classifications = React.useMemo<ClassificationDto[]>(
    () => classificationData ?? [],
    [classificationData],
  );
  const [availableLevels, setAvailableLevels] = React.useState<
    NonNullable<ClassificationDto['levels']>
  >([]);
  const updateUserRate = useUpdateUserRate();

  const formik = useFormik({
    initialValues: {
      classificationId: userRate.classificationId || '',
      classificationLevelId: userRate.classificationLevelId || '',
      rateImportKey: userRate.rateImportKey || '',
      hourlyRate: userRate.hourlyRate || 0,
      hireDate: userRate.hireDate ? new Date(userRate.hireDate) : null,
      healthInsurance: userRate.healthInsurance || 0,
      basicLife: userRate.basicLife || 0,
      accidentalDeath: userRate.accidentalDeath || 0,
      holiday: userRate.holiday || 0,
      _401k: userRate._401k || 0,
      vac: userRate.vac || 0,
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      setError(null);
      try {
        await updateUserRate.mutateAsync({
          id: userRate.id,
          request: {
            id: userRate.id,
            classificationId: values.classificationId || undefined,
            classificationLevelId: values.classificationLevelId || undefined,
            rateImportKey: values.rateImportKey || undefined,
            hourlyRate: values.hourlyRate,
            hireDate: values.hireDate || undefined,
            healthInsurance: values.healthInsurance,
            basicLife: values.basicLife,
            accidentalDeath: values.accidentalDeath,
            holiday: values.holiday,
            _401k: values._401k,
            vac: values.vac,
          },
        });

        onSave();
      } catch (err: unknown) {
        setError(getErrorMessage(err, 'Failed to save user rate'));
      }
    },
  });

  React.useEffect(() => {
    if (formik.values.classificationId) {
      const classification = classifications.find(
        (c) => c.id === formik.values.classificationId,
      );
      setAvailableLevels(classification?.levels || []);
    } else {
      setAvailableLevels([]);
    }
  }, [formik.values.classificationId, classifications]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      formik.handleSubmit();
    }
  };

  const calculateTotalHourlyBenefits = () => {
    return Number(
      Number(formik.values.vac || 0) +
        Number(formik.values.healthInsurance || 0) +
        Number(formik.values.basicLife || 0) +
        Number(formik.values.accidentalDeath || 0) +
        Number(formik.values.holiday || 0) +
        Number(formik.values._401k || 0),
    );
  };

  const annualSalary = formik.values.hourlyRate * 2080;

  return (
    <Stack gap="md">
      <Title order={5}>Basic Information</Title>

      <SimpleGrid cols={3}>
        <Select
          label="Classification"
          placeholder="Select classification"
          value={formik.values.classificationId}
          onChange={(value) => {
            formik.setFieldValue('classificationId', value || '');
            formik.setFieldValue('classificationLevelId', '');
          }}
          onBlur={formik.handleBlur}
          error={
            formik.touched.classificationId && formik.errors.classificationId
          }
          data={classifications.map((c) => ({
            value: c.id,
            label: c.name,
          }))}
          required
          onKeyDown={handleKeyDown}
          searchable
        />
        <Select
          label="Level"
          placeholder="Select level"
          value={formik.values.classificationLevelId}
          onChange={(value) =>
            formik.setFieldValue('classificationLevelId', value)
          }
          onBlur={formik.handleBlur}
          error={
            formik.touched.classificationLevelId &&
            formik.errors.classificationLevelId
          }
          data={availableLevels.map((level) => ({
            value: level.id,
            label: level.name,
          }))}
          onKeyDown={handleKeyDown}
          disabled={!formik.values.classificationId}
          searchable
          clearable
        />
        <DateInput
          label="Hire Date"
          placeholder="Select hire date"
          value={formik.values.hireDate}
          onChange={(value) => formik.setFieldValue('hireDate', value)}
          onBlur={formik.handleBlur}
          clearable
          onKeyDown={handleKeyDown}
          popoverProps={{ zIndex: 1304 }}
        />
      </SimpleGrid>

      <TextInput
        label="Rate Import Key"
        description="Copy and paste the 'Full Name' column from the user rate file for this user."
        placeholder="Enter rate import key"
        value={formik.values.rateImportKey}
        onChange={(e) => formik.setFieldValue('rateImportKey', e.target.value)}
        onBlur={formik.handleBlur}
        onKeyDown={handleKeyDown}
      />

      <Title order={5}>Rate Information</Title>

      <SimpleGrid cols={2}>
        <NumberInput
          label="Hourly Rate"
          placeholder="Enter hourly rate"
          prefix="$"
          decimalScale={3}
          description="Hourly rate for the employee"
          value={formik.values.hourlyRate}
          onChange={(value) => formik.setFieldValue('hourlyRate', value || 0)}
          onBlur={formik.handleBlur}
          error={formik.touched.hourlyRate && formik.errors.hourlyRate}
          min={0}
          required
          onKeyDown={handleKeyDown}
        />
        <TextInput
          label="Annual Salary"
          value={`$${annualSalary.toLocaleString()}`}
          disabled
          description="Calculated: Hourly Rate × 2080 hours"
        />
      </SimpleGrid>

      <Title order={5}>Benefits (Hourly)</Title>

      <SimpleGrid cols={2}>
        <NumberInput
          label="Health & Dental Insurance"
          placeholder="Enter amount"
          prefix="$"
          decimalScale={3}
          value={formik.values.healthInsurance}
          onChange={(value) =>
            formik.setFieldValue('healthInsurance', value || 0)
          }
          onBlur={formik.handleBlur}
          error={
            formik.touched.healthInsurance && formik.errors.healthInsurance
          }
          min={0}
          required
          onKeyDown={handleKeyDown}
        />
        <NumberInput
          label="Basic Life"
          placeholder="Enter amount"
          prefix="$"
          decimalScale={3}
          value={formik.values.basicLife}
          onChange={(value) => formik.setFieldValue('basicLife', value || 0)}
          onBlur={formik.handleBlur}
          error={formik.touched.basicLife && formik.errors.basicLife}
          min={0}
          required
          onKeyDown={handleKeyDown}
        />
      </SimpleGrid>

      <SimpleGrid cols={2}>
        <NumberInput
          label="Accidental Death & Disability"
          placeholder="Enter amount"
          prefix="$"
          decimalScale={3}
          value={formik.values.accidentalDeath}
          onChange={(value) =>
            formik.setFieldValue('accidentalDeath', value || 0)
          }
          onBlur={formik.handleBlur}
          error={
            formik.touched.accidentalDeath && formik.errors.accidentalDeath
          }
          min={0}
          required
          onKeyDown={handleKeyDown}
        />
        <NumberInput
          label="Holiday"
          placeholder="Enter amount"
          prefix="$"
          decimalScale={3}
          value={formik.values.holiday}
          onChange={(value) => formik.setFieldValue('holiday', value || 0)}
          onBlur={formik.handleBlur}
          error={formik.touched.holiday && formik.errors.holiday}
          min={0}
          required
          onKeyDown={handleKeyDown}
        />
      </SimpleGrid>

      <SimpleGrid cols={2}>
        <NumberInput
          label="401K"
          placeholder="Enter amount"
          prefix="$"
          decimalScale={3}
          value={formik.values._401k}
          onChange={(value) => formik.setFieldValue('_401k', value || 0)}
          onBlur={formik.handleBlur}
          error={formik.touched._401k && formik.errors._401k}
          min={0}
          required
          onKeyDown={handleKeyDown}
        />
        <NumberInput
          label="Vacation"
          placeholder="Enter amount"
          prefix="$"
          decimalScale={3}
          value={formik.values.vac}
          onChange={(value) => formik.setFieldValue('vac', value || 0)}
          onBlur={formik.handleBlur}
          error={formik.touched.vac && formik.errors.vac}
          min={0}
          required
          onKeyDown={handleKeyDown}
        />
      </SimpleGrid>

      <TextInput
        label="Total Hourly Benefits"
        value={formatRate(calculateTotalHourlyBenefits())}
        disabled
        description="Sum of all benefit amounts"
      />

      {error && (
        <Alert color="red" title="Error">
          {error}
        </Alert>
      )}

      <Group justify="flex-end" mt="md">
        <SecondaryButton onClick={onCancel}>Cancel</SecondaryButton>
        <SaveButton
          onClick={() => formik.handleSubmit()}
          disabled={!formik.isValid || !formik.dirty}
          loading={formik.isSubmitting}
        >
          Save User Rate
        </SaveButton>
      </Group>
    </Stack>
  );
};
